<template>
  <div>
    <collapse :active-index="0">
      <collapse-item>
        <h3 slot="title" class="mb-0">Información del Lote</h3>

        <div class="row">
          <div class="col-sm-12 col-md-5">
            <div>
              <p class="h5 mb-0">Nombre</p>
              <p>{{ plot.name }}</p>
            </div>

            <div>
              <p class="h5 mb-0">Productor</p>
              <router-link
                v-if="$can('show', 'producers')"
                :to="{
                  name: 'ProducerShow',
                  params: { id: plot.field.producer.id },
                }"
              >
                <p class="font-weight-600">
                  {{ plot.field.producer.businessName }}
                </p>
              </router-link>
              <p v-else class="font-weight-600">
                {{ plot.field.producer.businessName }}
              </p>
            </div>

            <div class="row">
              <div class="col-md-4 col-sm-12">
                <div>
                  <p class="h5 mb-0">Hectáreas</p>
                  <p>{{ plot.hectares }}</p>
                </div>
              </div>
              <div v-if="plot.groovesDistance" class="col-md-4 col-sm-12">
                <div>
                  <p class="h5 mb-0">Distancia entre surcos</p>
                  <p>{{ plot.groovesDistance | decimals }}&nbsp;cm.</p>
                </div>
              </div>
              <div class="col-4"></div>
            </div>
            <div class="row">
              <div class="col-sm-12 col-md-12">
                <div v-if="plot.latitude && plot.latitude">
                  <p class="h5 mb-0">Coordenadas</p>
                  <a target="_blank" :href="mapLink"> {{ plot.latitude }}°, {{ plot.longitude }}° </a>
                </div>
              </div>
            </div>
          </div>

          <div class="col-sm-12 col-md-6 col-lg-2">
            <div>
              <p class="h5 mb-0">Establecimiento</p>
              <router-link v-if="$can('show', 'fields')" :to="{ name: 'FieldShow', params: { id: plot.field.id } }">
                <p class="font-weight-600">{{ plot.field.name }}</p>
              </router-link>
            </div>

            <div>
              <p class="h5 mb-0">Variedad</p>
              <p class="mb-0">{{ plot.variety.name }}</p>
              <small class="m-0">{{ plot.variety.crop.name }}</small>
              <p class="small font-italic">Campaña {{ plot.campaign.name }}</p>
            </div>

            <div v-if="plot.plotType">
              <p class="h5 mb-0">Tipo de Lote</p>
              <p>{{ plot.plotType.label }}</p>
            </div>

            <div v-if="plot.predecessor">
              <p class="h5 mb-0">Cultivo anterior</p>
              <p>{{ plot.predecessor.name }}</p>
            </div>

            <div>
              <p class="h5 mb-0">Total de precipitaciones</p>
              <p>{{ plot.rainMeasuresTotal }} mm.</p>
            </div>
          </div>

          <div v-if="staticUrl" class="col-lg-5 col-md-6 col-sm-12 pt-2">
            <img class="m-auto" :src="staticUrl" alt="" style="max-width: 100%" />
          </div>
        </div>

        <div v-if="plot.latitude && plot.longitude" class="row">
          <div class="col-12 mt-3 d-flex follow-up-badges">
            <div class="mb-3 mr-5">
              <router-link
                :to="{
                  name: canGoToFollowUp(),
                  params: { id: plot.id },
                  query: { stage: getStage(plot) },
                }"
              >
                <badge v-if="plot.preEmergence" class="mb-0" type="success">
                  <span class="mr-1"> <i class="fas fa-check"></i> Datos Pre-Emergencia </span>
                </badge>
                <badge v-else class="mb-0" type="danger">
                  <span class="mr-1"> <i class="fas fa-times"></i> Datos Pre-Emergencia </span>
                </badge>
              </router-link>
            </div>
            <div class="mb-3 mr-5">
              <router-link
                :to="{
                  name: canGoToFollowUp(),
                  params: { id: plot.id },
                  query: { stage: getStage(plot) },
                }"
              >
                <badge v-if="plot.emergence" class="mb-0" type="success">
                  <span class="mr-1"> <i class="fas fa-check"></i> Datos Emergencia </span>
                </badge>
                <badge v-else class="mb-0" type="danger">
                  <span class="mr-1"> <i class="fas fa-times"></i> Datos Emergencia </span>
                </badge>
              </router-link>
            </div>
            <div class="mb-3 mr-5">
              <router-link
                :to="{
                  name: canGoToFollowUp(),
                  params: { id: plot.id },
                  query: { stage: getStage(plot) },
                }"
              >
                <badge v-if="plot.harvest" class="mb-0" type="success">
                  <span class="mr-1"> <i class="fas fa-check"></i> Datos Producción </span>
                </badge>
                <badge v-else class="mb-0" type="danger">
                  <span class="mr-1"> <i class="fas fa-times"></i> Datos Producción </span>
                </badge>
              </router-link>
            </div>
          </div>
        </div>
      </collapse-item>
    </collapse>
  </div>
</template>

<script>
import Collapse from '@/components/Collapse/Collapse';
import CollapseItem from '@/components/Collapse/CollapseItem';

export default {
  name: 'PlotData',
  components: {
    Collapse,
    CollapseItem,
  },

  props: {
    plot: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      staticUrl: null,
    };
  },

  computed: {
    mapLink() {
      const params = `/@${this.plot.latitude},${this.plot.longitude},10z`;
      return this.$services.resource('maps').staticPlace(this.plot.latitude, this.plot.longitude) + params;
    },
  },

  created() {
    this.staticUrl = this.getCompleteUrl();
  },

  methods: {
    canGoToFollowUp() {
      return (this.$can('update', 'plots') && !this.plot.isShare) || (this.plot.isShare && this.plot.write)
        ? 'PlotFollowUp'
        : null;
    },

    getStage(plot) {
      return plot.preEmergence ? (plot.emergence ? (plot.harvest ? 3 : 2) : 1) : 0;
    },

    getCompleteUrl() {
      if (this.plot.coordinates.length > 1) {
        return this.$services.resource('maps').staticMapWithPolygon({
          coordinates: this.plot.coordinates,
          zoom: 15,
        });
      } else if (this.plot.latitude && this.plot.longitude) {
        return this.$services.resource('maps').staticMap({
          lat: this.plot.latitude,
          lng: this.plot.longitude,
          zoom: 15,
        });
      }
      return null;
    },
  },
};
</script>

<style>
@media (max-width: 767px) {
  .follow-up-badges {
    flex-flow: column;
  }
}
</style>
